import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { faBasketShopping } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, type PropsWithChildren } from "react";
import { clsx } from "clsx";

const BottomActionsBar = ({ children }: PropsWithChildren) => (
  <div className="flex h-52 gap-5 rounded-t-3xl bg-kiosk-actions-sheet-background p-5">
    {children}
  </div>
);

export const BottomActionsCompactSlot = ({ children }: PropsWithChildren) => (
  <div className="flex w-44 shrink-0">{children}</div>
);

export const BottomActionsChoiceSlot = ({ children }: PropsWithChildren) => (
  <div className="flex w-56 shrink-0 flex-col gap-3">{children}</div>
);

type BottomActionsProps = PropsWithChildren<{
  primaryButton: React.ReactNode;
  secondaryButton: React.ReactNode;
}>;

export const BottomActions = ({
  primaryButton,
  secondaryButton,
  children
}: BottomActionsProps) => {
  return (
    <BottomActionsBar>
      <BottomActionsCompactSlot>{secondaryButton}</BottomActionsCompactSlot>
      <div className="flex flex-1">{primaryButton}</div>
      {children}
    </BottomActionsBar>
  );
};

type PayButtonProps = PropsWithChildren<{
  onClick: () => void;
  totalAmount?: string;
  disabled?: boolean;
}>;

export const PayButton = ({
  onClick,
  totalAmount,
  children,
  disabled
}: PayButtonProps) => (
  <button
    type="button"
    className="flex w-full items-center justify-between rounded-3xl bg-kiosk-actions-sheet-primary-background px-10 py-9 text-kiosk-actions-sheet-primary-text"
    onClick={onClick}
    disabled={disabled}
  >
    <span className="text-4xl font-extrabold">{children}</span>
    {totalAmount ? (
      <span className="text-5xl font-extrabold">{totalAmount}</span>
    ) : null}
  </button>
);

type SecondaryButtonProps = PropsWithChildren<{
  onClick?: () => void;
  icon: IconProp;
  className?: string;
  disabled?: boolean;
}>;

export const SecondaryButton = forwardRef<
  HTMLButtonElement,
  SecondaryButtonProps
>(({ onClick, icon, children, className, disabled }, ref) => (
  <button
    ref={ref}
    type="button"
    className={clsx(
      "flex w-full items-center justify-center gap-x-5 rounded-3xl bg-kiosk-actions-sheet-secondary-background px-4 text-kiosk-actions-sheet-secondary-text",
      className
    )}
    onClick={onClick}
    disabled={disabled}
  >
    <FontAwesomeIcon
      icon={icon}
      fontSize={48}
      className="text-kiosk-actions-sheet-secondary-text"
    />
    <span className="text-3xl font-semibold text-kiosk-actions-sheet-secondary-text">
      {children}
    </span>
  </button>
));

SecondaryButton.displayName = "SecondaryButton";

export const NavigationButton = ({
  onClick,
  totalAmount,
  numberOfProducts,
  ariaLabel
}: {
  onClick: () => void;
  totalAmount?: string;
  numberOfProducts: string;
  ariaLabel?: string;
}) => (
  <button
    type="button"
    aria-label={ariaLabel}
    className="flex w-full items-center gap-x-8 rounded-3xl bg-kiosk-actions-sheet-primary-background px-10 py-9 text-left text-kiosk-actions-sheet-primary-text"
    onClick={onClick}
  >
    <FontAwesomeIcon icon={faBasketShopping} fontSize={56} />
    <div className="flex flex-col">
      {totalAmount ? (
        <span className="text-5xl font-extrabold">{totalAmount}</span>
      ) : null}
      <span className="text-3xl font-normal">{numberOfProducts}</span>
    </div>
  </button>
);
